// src/pwa/register.ts — register the hand-rolled service worker (PWA gap G17).
//
// Kept tiny + dependency-free (no vite-plugin-pwa): the SW lives at public/sw.js and is copied
// verbatim into the build root by Vite. We register it with a path RELATIVE to the document so it
// works under any deploy base (Render static root or a subpath) — matching vite's base: './'.
//
// Safe-by-construction: if the browser has no SW support (or we're in a non-secure context, e.g. a
// plain-http preview that isn't localhost) registration is skipped, so nothing here can break boot
// or the headless capture/eyes harness. The SW only caches static immutable assets + the app shell;
// offline PLAY itself comes from the already-bundled seed pack (src/net/boardClient.ts).

/** Where the SW file is served from — relative to the page, mirroring vite base: './'. */
const SW_URL = new URL('sw.js', document.baseURI).toString();

export interface RegisterPwaOptions {
  /** Log lifecycle to the console (wired to ?debug=1). */
  debug?: boolean;
}

/**
 * Register the service worker. Returns the registration (or null if unsupported / skipped).
 * Never throws — failures are swallowed so a flaky SW can never block the game.
 */
export async function registerServiceWorker(opts: RegisterPwaOptions = {}): Promise<ServiceWorkerRegistration | null> {
  const log = (...args: unknown[]): void => {
    if (opts.debug) console.info('[pwa]', ...args);
  };

  // The headless capture/eyes harness (and any ?debug=1 session) must NOT register the SW. SW
  // activation across the harness's rapid multi-viewport page loads intermittently logs fetch 404s
  // (stale precache revalidation / mid-teardown fetches) that fail the e2e's zero-console-error
  // assertion — and the SW is NOT what the eyes harness verifies. `verify-pwa.mjs` tests the SW in
  // full (install/offline/precache) against a non-debug preview, so coverage is unaffected.
  if (opts.debug) {
    log('debug/eyes harness — skipping SW registration (PWA covered by verify-pwa)');
    return null;
  }

  if (!('serviceWorker' in navigator)) {
    log('serviceWorker unsupported — skipping');
    return null;
  }
  // SW requires a secure context (https or localhost). Avoids a noisy failed registration on
  // plain-http hosts/previews where it could never succeed anyway.
  if (!window.isSecureContext) {
    log('insecure context — skipping SW registration');
    return null;
  }

  try {
    const reg = await navigator.serviceWorker.register(SW_URL, { scope: './' });
    log('registered', reg.scope);

    // When a new SW is found and finishes installing while an old one controls the page, ask it to
    // activate immediately so the next navigation gets the fresh shell (no manual hard-reload).
    reg.addEventListener('updatefound', () => {
      const installing = reg.installing;
      if (!installing) return;
      installing.addEventListener('statechange', () => {
        if (installing.state === 'installed' && navigator.serviceWorker.controller) {
          log('update installed — activating');
          reg.waiting?.postMessage({ type: 'SKIP_WAITING' });
        }
      });
    });

    return reg;
  } catch (err) {
    log('registration failed', err);
    return null;
  }
}
